import { useLazyQuery } from '@apollo/client/react';
import { usePathname } from 'expo-router';
import { createContext, useContext, useEffect } from 'react';

import type { ContainerComponent } from '../../common/types/Common';
import { captureWarning } from '../../logger/Logger';
import useGetHolisticSummary from '../hooks/useGetHolisticSummary';
import {
    BalancesNotReadyStatuses,
    GET_CONNECTION_QUERY,
    type QuilttConnectionResponse,
} from '../types/Quiltt';

const POLLING_INTERVAL = 1000;

const QuilttConnectionStatusContext = createContext<
    ((connectionId: string) => void) | null
>(null);

export const useWatchQuilttConnectionStatus = (): ((
    connectionId: string,
) => void) => {
    const contextValue = useContext(QuilttConnectionStatusContext);

    if (!contextValue) {
        throw new Error(
            'useWatchQuilttConnectionStatus must be used within a QuilttConnectionStatusProvider',
        );
    }

    return contextValue;
};

// this component provides a function that allows us to poll quiltt for connection statuses and refresh the holistic summary once balances are available
const QuilttConnectionStatusProvider: ContainerComponent = ({ children }) => {
    const { refetch } = useGetHolisticSummary();
    const pathname = usePathname();

    const [watchConnection, { stopPolling, variables, data, error }] =
        useLazyQuery<QuilttConnectionResponse>(GET_CONNECTION_QUERY, {
            pollInterval: POLLING_INTERVAL,
            fetchPolicy: 'no-cache',
            notifyOnNetworkStatusChange: true,
        });

    useEffect(() => {
        if (error) {
            captureWarning(
                `Error checking quiltt connection status: ${JSON.stringify(variables)}`,
                error,
            );
        }

        if (!data) return;

        // we're waiting for the connection to finish syncing
        if (BalancesNotReadyStatuses.includes(data.connection.status)) return;

        // the connection has finished syncing, let's re-fetch the holistic summary
        stopPolling();

        // No need to refetch holistic for the External Accounts page in Settings, since its done immediately after linking and we obtain no new information from this polling.
        const shouldRefetchHolistic = pathname !== '/profile/external-accounts';

        if (shouldRefetchHolistic) {
            void refetch();
        }
    }, [data, error, stopPolling, pathname, refetch]);

    return (
        <QuilttConnectionStatusContext.Provider
            value={(connectionId: string) => {
                void watchConnection({ variables: { connectionId } });
            }}
        >
            {children}
        </QuilttConnectionStatusContext.Provider>
    );
};

export default QuilttConnectionStatusProvider;
